import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen, CheckCircle2, Circle, Clock, GraduationCap, History,
  Puzzle, PenTool, ClipboardList, Sparkles, TrendingDown, TrendingUp, Minus, Flame,
} from "lucide-react";
import { AskButton } from "../ToolResultRenderer";

const ACTIVITY_ICON: Record<string, any> = {
  practice_test: ClipboardList,
  ai_practice_test: Sparkles,
  worksheet: BookOpen,
  game: Puzzle,
  writing: PenTool,
  review: History,
  lesson: GraduationCap,
};

function activityPrompt(kind: string, grade?: string | null, subject?: string | null, title?: string) {
  const g = grade ?? "4";
  const s = subject ?? "";
  const where = "here in the chat";
  switch (kind) {
    case "practice_test": return `Run a Grade ${g} ${s} practice test ${where}${title ? ` for this step: ${title}` : ""}`;
    case "ai_practice_test": return `Generate an AI practice test for Grade ${g} ${s} ${where}${title ? ` for this step: ${title}` : ""}`;
    case "worksheet": return `Open a Grade ${g} ${s} worksheet ${where}`;
    case "game": return `Open Grade ${g} ${s} learning games ${where}`;
    case "writing": return `Open the Grade ${g} writing pad ${where}`;
    default: return `Show my progress report ${where}`;
  }
}

/** Saved, trackable course with per-step completion. */
export function CoursePlanCard({ data }: { data: any }) {
  const [steps, setSteps] = useState<any[]>(data.steps ?? []);
  const total = steps.length || 1;
  const done = steps.filter((s) => s.status === "completed" || s.status === "skipped").length;
  const progress = Math.round((done / total) * 100);

  const toggle = (i: number) =>
    setSteps((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, status: s.status === "completed" ? "pending" : "completed" } : s)),
    );

  return (
    <Card className="overflow-hidden border-primary/30">
      <div className="bg-gradient-to-r from-primary/15 to-transparent px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Course{data.learner && data.learner !== "you" ? ` · for ${data.learner}` : ""}
            </p>
            <h3 className="font-semibold leading-tight">{data.title}</h3>
            {data.goal && <p className="text-sm text-muted-foreground mt-0.5">{data.goal}</p>}
          </div>
          <Badge variant="secondary" className="shrink-0">{data.subject}</Badge>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Progress value={progress} className="h-2" />
          <span className="text-xs text-muted-foreground shrink-0">{progress}%</span>
        </div>
      </div>
      <CardContent className="p-0 divide-y">
        {steps.map((s, i) => {
          const Icon = ACTIVITY_ICON[s.activity_kind] ?? ClipboardList;
          const complete = s.status === "completed";
          return (
            <div key={s.id ?? i} className="flex items-start gap-3 px-4 py-3">
              <button
                onClick={() => toggle(i)}
                className="mt-0.5 text-muted-foreground hover:text-primary transition-colors"
                aria-label={complete ? "Mark step incomplete" : "Mark step complete"}
              >
                {complete ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">{s.day_label}</span>
                  {s.target_minutes ? (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />{s.target_minutes}m
                    </span>
                  ) : null}
                </div>
                <p className={`text-sm font-medium ${complete ? "line-through text-muted-foreground" : ""}`}>{s.title}</p>
                {s.note && <p className="text-xs text-muted-foreground mt-0.5">{s.note}</p>}
              </div>
              <AskButton
                size="sm"
                variant={complete ? "ghost" : "secondary"}
                className="shrink-0"
                icon={<Icon className="h-3.5 w-3.5" />}
                label="Start"
                prompt={activityPrompt(s.activity_kind, data.grade, s.subject, s.title)}
              />
            </div>
          );
        })}
      </CardContent>
      {data.rationale && (
        <div className="px-4 py-3 bg-muted/40 text-xs text-muted-foreground">{data.rationale}</div>
      )}
    </Card>
  );
}

export function CourseListCard({ paths }: { paths: any[] }) {
  if (!paths.length) {
    return <Card><CardContent className="p-4 text-sm text-muted-foreground">No saved courses yet.</CardContent></Card>;
  }
  return (
    <div className="space-y-2">
      {paths.map((p) => (
        <Card key={p.id}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{p.title}</p>
              <p className="text-xs text-muted-foreground truncate">{p.subject}{p.grade ? ` · Grade ${p.grade}` : ""} · {p.goal}</p>
              <Progress value={Number(p.progress_pct ?? 0)} className="h-1.5 mt-2" />
            </div>
            <Badge variant={p.status === "completed" ? "secondary" : "default"} className="shrink-0">
              {Math.round(Number(p.progress_pct ?? 0))}%
            </Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

const TREND_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus, new: Sparkles } as const;

export function ProgressReportCard({ data }: { data: any }) {
  if (data.not_signed_up) {
    return (
      <Card><CardContent className="p-4 text-sm text-muted-foreground">
        {data.who} hasn't signed up yet, so there's no activity to report on.
      </CardContent></Card>
    );
  }
  const subjects: any[] = data.per_subject ?? [];
  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {data.who ?? "Your"} progress · last {data.window_days} days
            </p>
            <p className="text-2xl font-semibold">
              {data.overall_percent != null ? `${data.overall_percent}%` : "—"}
              <span className="text-sm font-normal text-muted-foreground ml-2">
                across {data.total_activities} activities
              </span>
            </p>
          </div>
          {data.streak_days > 1 && (
            <Badge variant="secondary" className="gap-1"><Flame className="h-3.5 w-3.5" />{data.streak_days}-day streak</Badge>
          )}
        </div>

        {subjects.length ? (
          <div className="space-y-2">
            {subjects.map((s) => {
              const Icon = TREND_ICON[s.trend as keyof typeof TREND_ICON] ?? Minus;
              return (
                <div key={s.subject} className="flex items-center gap-3">
                  <span className="text-sm w-32 shrink-0 truncate">{s.subject}</span>
                  <Progress value={s.avgPercent} className="h-2 flex-1" />
                  <span className="text-sm tabular-nums w-10 text-right">{s.avgPercent}%</span>
                  <span className={`inline-flex items-center gap-0.5 text-xs w-14 justify-end ${
                    s.trend === "up" ? "text-emerald-600" : s.trend === "down" ? "text-destructive" : "text-muted-foreground"
                  }`}>
                    <Icon className="h-3.5 w-3.5" />
                    {s.trend === "new" ? "new" : s.delta > 0 ? `+${s.delta}` : s.delta}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No completed activities in this window yet.</p>
        )}

        <div className="flex gap-2 flex-wrap text-xs">
          {data.weakest && <Badge variant="outline">Focus: {data.weakest.subject}</Badge>}
          {data.strongest && <Badge variant="outline">Strongest: {data.strongest.subject}</Badge>}
          {data.most_improved && <Badge variant="outline">Most improved: {data.most_improved.subject} +{data.most_improved.delta}</Badge>}
        </div>
      </CardContent>
    </Card>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  chat_turn: "Conversation",
  test_result: "Test",
  game_result: "Game",
  worksheet_result: "Worksheet",
  writing_result: "Writing",
  plan_milestone: "Course",
  profile_fact: "Profile",
};

export function HistoryRecallCard({ data }: { data: any }) {
  const hits: any[] = data.hits ?? [];
  if (!hits.length) {
    return (
      <Card><CardContent className="p-4 text-sm text-muted-foreground">
        Nothing on record for that yet.
      </CardContent></Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1.5">
          <History className="h-3.5 w-3.5" /> From {data.subject === "you" ? "your" : `${data.subject}'s`} history
        </p>
        <ul className="space-y-2">
          {hits.map((h, i) => (
            <li key={i} className="text-sm border-l-2 border-primary/40 pl-3">
              <span className="text-xs text-muted-foreground">
                {new Date(h.date).toLocaleDateString()} · {SOURCE_LABEL[h.source] ?? h.source}
              </span>
              <p className="text-muted-foreground">{h.content}</p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function StepUpdateCard({ data }: { data: any }) {
  return (
    <Card className="border-primary/30">
      <CardContent className="p-4 flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">Step {data.status === "completed" ? "completed" : data.status}</p>
          <Progress value={data.progress_pct} className="h-1.5 mt-2" />
        </div>
        <span className="text-sm tabular-nums">{data.progress_pct}%</span>
      </CardContent>
    </Card>
  );
}
